/**
 * @file    fieldValidation.ts
 * @scope   SHARED
 * @summary Pure predicates/helpers deciding whether a configured field (and its filters) is
 *          "complete" enough to keep or serialize, plus the strip helpers the reducer uses.
 * @consumes SelectedFieldModel/SelectedUserFields, filterValidation strippers
 * @produces `isPercentageSettingsIncomplete`, `isFieldItemInvalid`, field/filter strippers
 * @sideEffects None — pure, no React. Safe to reuse anywhere.
 * @notes Complements `filterValidation.ts` and `partitionValidation.ts`.
 */
import { Tab } from 'components/TabNavigator/TabNavigator';
import { SelectedFieldModel, SelectedUserFields } from 'models/Enterprise/FieldsManagerModel';
import { hasIncompleteFilters, stripIncompleteFilters } from './filterValidation';
import {
  isNumericSettingsComplete,
  usesNumericSettings,
} from '../views/partitionsManager/calculationsManager/numericSettingsUtils';

/**
 * Checks whether a Percentage calculation field is missing its required selected values.
 * Numeric fields: Percentage requires complete numeric settings; Frequency only fails when partially filled.
 * @param field - The field entry to validate
 * @returns true if the field's advanced settings are incomplete
 */
export const isPercentageSettingsIncomplete = (field: SelectedFieldModel): boolean => {
  const type = String(field.calculationType?.type);
  if (usesNumericSettings(field)) {
    if (type === '2') return !isNumericSettingsComplete(field.numericSettings);
    if (type === '4')
      return (
        (field.numericSettings?.values.length ?? 0) > 0 &&
        !isNumericSettingsComplete(field.numericSettings)
      );
    return false;
  }
  return (
    type === '2' &&
    (!field.percentageSettings?.selectedValues ||
      field.percentageSettings.selectedValues.length === 0)
  );
};

/**
 * Validates that a field entry has all required values filled in:
 * display name, selected field, calculation type, and percentage settings (when applicable).
 * @param field - The field entry to validate
 * @returns true if any required value is missing
 */
export const fieldItemHasMissingRequiredValues = (field: SelectedFieldModel): boolean => {
  if (field.isProjectFrequency) {
    return !field.displayName?.trim();
  }

  return (
    !field.displayName?.trim() ||
    !field.field?.fieldGuid ||
    !field.calculationType ||
    isPercentageSettingsIncomplete(field)
  );
};

/**
 * Determines whether a field entry should be shown as invalid.
 * A field is invalid when it has been touched by the user and has missing required values.
 * @param field - The field entry to validate
 * @param index - The positional index of the field in the list
 * @param touchedFieldIndices - Set of indices the user has interacted with
 * @returns true if the field is touched and has missing required values
 */
export const isFieldItemInvalid = (
  field: SelectedFieldModel,
  index: number,
  touchedFieldIndices: Set<number>
): boolean => touchedFieldIndices.has(index) && fieldItemHasMissingRequiredValues(field);

/**
 * Returns a copy of the user fields collection with incomplete entries removed.
 * @param userFields - The field group to filter
 * @returns A new SelectedUserFields containing only valid field entries
 */
export const stripInvalidFieldsFromUserFields = (
  userFields: SelectedUserFields
): SelectedUserFields => ({
  ...userFields,
  fields: userFields.fields.filter(
    (field) => field.isProjectFrequency || !fieldItemHasMissingRequiredValues(field)
  ),
});

/**
 * Checks whether the collection holds at least one incomplete filter, either at
 * partition level or inside any of its fields.
 * @param userFields - The field group to inspect
 * @returns true if any partition filter or field filter is incomplete
 */
export const hasInvalidFiltersInUserFields = (userFields: SelectedUserFields): boolean =>
  hasIncompleteFilters(userFields.partitionFilters ?? []) ||
  userFields.fields.some((field) => hasIncompleteFilters(field.filters ?? []));

/**
 * Returns a copy of the user fields collection with incomplete filters removed,
 * both at partition level and inside every field entry.
 * Mirrors `stripInvalidFieldsFromUserFields` but for filters.
 * @param userFields - The field group to filter
 * @returns A new SelectedUserFields containing only fully configured filters
 */
export const stripInvalidFiltersFromUserFields = (
  userFields: SelectedUserFields
): SelectedUserFields => ({
  ...userFields,
  partitionFilters: stripIncompleteFilters(userFields.partitionFilters ?? []),
  fields: userFields.fields.map((field) => ({
    ...field,
    filters: stripIncompleteFilters(field.filters ?? []),
  })),
});

/**
 * Returns the stripped SelectedUserFields when the FieldsManager tab is popped from
 * the navigator history, or null if no stripping is needed.
 * Both invalid fields and invalid filters (partition-level and field-level) are dropped.
 */
export const getStrippedFieldsOnTabPop = (
  poppedTabs: Tab[],
  fieldsManagerTabName: string,
  userFields: SelectedUserFields | null
): SelectedUserFields | null => {
  if (!poppedTabs.some((t) => t.name === fieldsManagerTabName)) return null;
  if (!userFields || userFields.fromEditMode) return null;

  const strippedFields = stripInvalidFieldsFromUserFields(userFields);
  const hasRemovedFields = strippedFields.fields.length !== userFields.fields.length;
  const hasRemovedFilters = hasInvalidFiltersInUserFields(strippedFields);

  if (!hasRemovedFields && !hasRemovedFilters) return null;

  return hasRemovedFilters ? stripInvalidFiltersFromUserFields(strippedFields) : strippedFields;
};
